import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock } from 'lucide-react';
import type { Badge, CivicLevel } from '../lib/civicLevelTypes';
import { getAllBadges, getUserBadges, LEVEL_THRESHOLDS, LEVEL_ORDER } from '../lib/civicLevelService';

interface BadgesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentXP?: number;
}

export default function BadgesModal({ isOpen, onClose, userId, currentXP = 0 }: BadgesModalProps) {
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [earnedBadgeIds, setEarnedBadgeIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    loadBadges();

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, userId, currentXP, onClose]);

  async function loadBadges() {
    setLoading(true);
    const all = await getAllBadges();

    // Calculate earned badges based on current XP instead of database
    const earnedIds = new Set(
      all
        .filter(badge => currentXP >= badge.xp_threshold)
        .map(b => b.id)
    );

    setAllBadges(all);
    setEarnedBadgeIds(earnedIds);
    setLoading(false);
  }

  if (!isOpen) return null;

  const nextBadge = allBadges.find(badge => badge.xp_threshold > currentXP);

  const getUnlockHint = (badge: Badge): string => {
    if (badge.xp_threshold === 0) {
      return 'Starting level';
    }
    if (badge.id === nextBadge?.id) {
      return `Unlocks at ~${badge.xp_threshold} CP`;
    }
    return 'Unlocks with continued engagement';
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        '--bottom-nav-offset': 'calc(72px + env(safe-area-inset-bottom))',
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(4px)',
        padding: '72px 16px',
        paddingBottom: 'calc(72px + var(--bottom-nav-offset))',
      } as React.CSSProperties}
      onClick={onClose}
    >
      <div
        className="relative bg-[#141414] rounded-2xl border flex flex-col overflow-hidden"
        style={{
          width: 'min(560px, 100%)',
          maxHeight: 'calc(100vh - 144px - var(--bottom-nav-offset))',
          borderColor: 'rgba(64, 224, 208, 0.55)',
          boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex-shrink-0 bg-[#141414] border-b px-5 pt-5 pb-4 z-10 rounded-t-2xl" style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-semibold text-[#F5F5F5]" style={{ letterSpacing: '-0.005em' }}>
              Your badges
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#808080] hover:text-[#A0A0A0]" />
            </button>
          </div>
          <p className="text-[13px] text-[#A0A0A0] mt-2">
            Badges mark key moments in your civic journey. You'll unlock new ones as your momentum grows.
          </p>
        </div>

        <div
          className="flex-1 px-5 pt-4 overflow-y-auto"
          style={{
            minHeight: 0,
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div
                className="w-8 h-8 border-4 rounded-full animate-spin"
                style={{
                  borderColor: '#40E0D0',
                  borderTopColor: 'transparent',
                }}
              ></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {allBadges.map((badge) => {
                const isEarned = earnedBadgeIds.has(badge.id);
                const isNext = !isEarned && badge.id === nextBadge?.id;

                return (
                  <div
                    key={badge.id}
                    className="relative rounded-xl border"
                    style={{
                      backgroundColor: isEarned ? '#1A1A1A' : isNext ? 'rgba(21, 21, 21, 0.75)' : 'rgba(21, 21, 21, 0.6)',
                      borderColor: isEarned ? 'rgba(64, 224, 208, 0.65)' : isNext ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.4)',
                      padding: window.innerWidth < 640 ? '14px' : '17px',
                    }}
                  >
                    <div className="flex items-start gap-2.5 mb-2">
                      <div
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-lg sm:text-xl transition-transform flex-shrink-0 ${
                          isEarned ? 'scale-100' : 'scale-90 saturate-50 opacity-60'
                        }`}
                        style={{
                          background: isEarned
                            ? `linear-gradient(135deg, ${badge.color_primary}, ${badge.color_secondary})`
                            : '#2a2a2a',
                        }}
                      >
                        {badge.icon_symbol}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-[14px] sm:text-[15px] font-semibold text-[#F5F5F5] truncate">
                            {badge.level_name}
                          </h3>
                          {badge.level_number === 6 && isEarned && (
                            <span
                              className="text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full border flex-shrink-0"
                              style={{
                                backgroundColor: 'rgba(64, 224, 208, 0.15)',
                                color: '#40E0D0',
                                borderColor: 'rgba(64, 224, 208, 0.55)',
                              }}
                            >
                              Max
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] sm:text-[11px] text-[#A0A0A0]">
                          Level {badge.level_number}{badge.xp_threshold === 0 ? ' • Starting level' : ''}
                        </p>
                      </div>
                    </div>

                    <p className="text-[12px] sm:text-[13px] text-[#CCCCCC] leading-snug mb-2 line-clamp-2">
                      {badge.description}
                    </p>

                    <div className="mt-2 pt-2 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                      {isEarned ? (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#40E0D0' }}></div>
                          <span className="text-[10px] sm:text-[11px] font-bold" style={{ color: '#40E0D0' }}>
                            Achieved
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-0.5">
                            <Lock className="w-3 h-3 text-[#666666]" />
                            <span className="text-[10px] sm:text-[11px] text-[#666666] font-medium">
                              {isNext ? 'Next unlock' : 'Locked'}
                            </span>
                          </div>
                          <p className="text-[10px] sm:text-[11px]" style={{ color: isNext ? '#A0A0A0' : '#666666' }}>
                            {getUnlockHint(badge)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
